define([
  'articles',
  'moment',
  'underscore',
], (articles, moment, _) => {

  // Date/Timezone Helper Func.
  const parseDateTimezone = (dtz) => {
    try {
      // Get current user timezone. If not able to, set to GMT
      let currentTimezone = moment.tz.zone(moment.tz.guess());
      if (!(currentTimezone && currentTimezone.offsets)) {
        currentTimezone = moment.tz.zone('GMT');
      }
      // Parse string into time and timezone objects and set all spaces to underscores
      const currentRuleFilterObj = JSON.parse(dtz.filter_value);
      currentRuleFilterObj.timezone = currentRuleFilterObj.timezone.replace(/ /g, '_');
      // Return new moment of difference between GMT moment and selected timezone in relation to user timezone
      return moment(currentRuleFilterObj.time);
    } catch (e) {
      console.log('Failed to parse date timezone: ', dtz, e);

      return moment().utc();
    }
  };

  const isCurrentUrlAllowed = (rules, geoLocation, pageviews, opts = {}) => {
    const {
      popupForm = null,
      cartTotal = 0,
      cartItems = [],
    } = opts;

    const where = (array, key, value, findWhere) => {
      const returnArray = [];
      if (array && array.length) {
        for (let i = 0; i < array.length; i++) {
          const obj = array[i];
          if (typeof(obj) === 'object' && obj.hasOwnProperty(key) && obj[key] === value) {
            returnArray.push(obj);
            if (findWhere) {
              return returnArray[0];
            }
          }
        }
      }
      if (findWhere) {
        return null;
      }
      return returnArray;
    };

    if (rules && rules.length) {
      const needsArticles = where(rules, 'filter_type', 'article', true);
      let articlesExist;
      if (needsArticles) {
        articlesExist = articles.hasArticles();
      }

      if (!geoLocation) {
        geoLocation = false;
      }

      //  Underscore version is old. Take matches from new version.
      const pairs = (obj) => {
        const keys = Object.keys(obj);
        const length = keys.length;
        const curPairs = new Array(length);
        for (let i = 0; i < length; i++) {
          curPairs[i] = [keys[i], obj[keys[i]]];
        }
        return curPairs;
      };

      const matches = (attrs) => {
        const curPairs = pairs(attrs);
        const length = curPairs.length;
        return (obj) => {
          if (obj === null) return !length;
          obj = new Object(obj);
          for (let i = 0; i < length; i++) {
            const pair = curPairs[i];
            const key = pair[0];
            if (pair[1] !== obj[key] || !(key in obj)) return false;
          }
          return true;
        };
      };

      // For query string parsing
      const queryStringParser = (queryString) => {
        queryString = queryString.substr(1).split('&');
        if (queryString === "") return {};
        const queryDict = {};
        for (let i = 0; i < queryString.length; ++i) {
          const p = queryString[i].split('=', 2);
          if (p.length === 1)
            queryDict[p[0]] = "";
          else {
            try {
              queryDict[p[0]] = decodeURIComponent(p[1].replace(/\+/g, " "));
            } catch (e) {
              queryDict[p[0]] = p[1].replace(/\+/g, " ");
            }
          }
        }
        return queryDict;
      };

      const safeTicks = (strings, ...interpolatedValues) => {
        return strings.reduce((total, current, index) => {
          total += current;
          if (interpolatedValues.hasOwnProperty(index)) {
            total += String(interpolatedValues[index]).replace(/'/g, '\'');
          }
          return total;
        }, '');
      }

      const specialMatching = (rule, hash, search) => {
        if (rule.has_hash) {
          if (rule.hash != hash) {
            return false;
          }
        }
        if (rule.has_query_params) {
          const matchQuery = queryStringParser(rule.search);
          const currentQuery = queryStringParser(search);

          const isIn = matches(matchQuery);
          if (isIn(currentQuery)) {
            return true;
          }
          if (['containing', 'beginning'].indexOf(rule.filter_type) >= 0) {
            const allMatched = true;
            const keys = Object.keys(matchQuery);
            for (let i = 0; i < keys.length; i++) {
              const key = keys[i];
              const val = matchQuery[keys[i]];
              if (val && val.length) {
                if (key in currentQuery) {
                  if (currentQuery[key].indexOf(val) != 0) {
                    return false;
                  }
                } else if (rule.filter_type === 'containing') {
                  let anyMatched = false;
                  Object.keys(currentQuery).forEach((currentQueryKey) => {
                    if (currentQueryKey.lastIndexOf(key) >= 0 && key === currentQueryKey.substring(currentQueryKey.lastIndexOf(key)) && currentQuery[currentQueryKey].indexOf(val) === 0) {
                      anyMatched = true;
                    }
                  });
                  if (!anyMatched) {
                    return false;
                  }
                } else {
                  return false;
                }
              } else {
                if (key in currentQuery) {
                  continue;
                } else {
                  const currentKeys = Object.keys(currentQuery);
                  let anyMatched = false;
                  for (let j = 0; j < currentKeys.length; j++) {
                    const currentKey = currentKeys[j];
                    if (currentKey.indexOf(key) === 0) {
                      anyMatched = true;
                      break;
                    }
                  }
                  if (!anyMatched) {
                    return false;
                  }
                }
              }
            }
          } else {
            return false;
          }
        }
        return true;
      }

      const parsedUserAgent = where(rules, 'parsedUserAgent', true, true);

      let loc = window.location.toString();
      if (loc) {
        loc = loc.toLowerCase();
      }
      let pathname = window.location.pathname.toString();
      if (pathname) {
        pathname = pathname.toLowerCase();
      }
      // Ignore protocol
      let path = window.location.hostname + pathname;
      if (path) {
        path = path.toLowerCase();
      }
      // Strip ending / if present
      if (pathname[pathname.length - 1] === '/') {
        pathname = pathname.substring(0, pathname.length - 1);
        path = path.substring(0, path.length - 1);
      }
      // Strip www if present
      if (path.indexOf('www.') === 0) {
        path = path.substring(4);
      }
      let search = window.location.search;
      if (search) {
        search = search.toLowerCase();
      }
      let hash = window.location.hash;
      if (hash) {
        hash = hash.toLowerCase();
        if (hash[0] != '#') {
          hash = '#' + hash;
        }
      }
      const currentQuery = queryStringParser(search);
      const width = Math.min(window.screen.availWidth || document.documentElement.clientWidth, document.documentElement.clientWidth);
      const cookies = document.cookie.split(';');
      const cookieDict = {};
      const cookieKeysOnly = [];

      for (let i = 0; i < cookies.length; i++) {
        const key = cookies[i].substring(0, cookies[i].indexOf('=')).trim();
        const val = cookies[i].substring(cookies[i].indexOf('=') + 1).trim();
        cookieKeysOnly.push(key);
        cookieDict[key] = val;
      }

      let referrer = document.referrer;
      if (referrer && referrer.length) {
        referrer = referrer.toLowerCase();
        try {
          const referrerUri = URI(referrer);
          if (referrerUri.is('ip')) {
            let referrerHostname = referrerUri.hostname();
            if (referrerHostname) {
              referrerHostname = referrerHostname.toLowerCase();
            }
          } else {
            let referrerHostname = referrerUri.domain();
            if (referrerHostname) {
              referrerHostname = referrerHostname.toLowerCase();
            }
          }
          let referrerPathname = referrerUri.path();
          if (referrerPathname) {
            referrerPathname = referrerPathname.toLowerCase();
          }
          let referrerTld = referrerUri.tld();
          if (referrerTld) {
            referrerTld = referrerTld.toLowerCase();
          }
          let referrerMatchUrl = (referrerUri.hostname()) ? referrerUri.hostname().toLowerCase() + referrerPathname : referrerPathname;

          if (referrerPathname[referrerPathname.length - 1] === '/') {
            referrerPathname = referrerPathname.substring(0, referrerPathname.length - 1);
            referrerMatchUrl = referrerMatchUrl.substring(0, referrerMatchUrl.length - 1);
          }
          if (referrerMatchUrl.indexOf('www.') === 0) {
            referrerMatchUrl = referrerMatchUrl.substring(4);
          }

          const referrerSearch = referrerUri.search();
          const referrerHash = referrerUri.hash();
        } catch (err) {
          referrer = false;
        }
      } else {
        referrer = false;
      }

      const checkGroups = (groups, exclude, allRules) => {
        let returnValue = false;
        for (const i in groups) {
          const group = groups[i];
          let anyFail = false;
          group.forEach((rule) => {
            let currentRuleMatch = false;
            if (!anyFail) {
              if (rule.filter_type.indexOf('cart_total') === 0) {
                const type = rule.filter_type.split('_')[2];
                const amount = parseFloat(rule.filter_value);
                if (type === 'lt' && cartTotal < amount) currentRuleMatch = true;
                if (type === 'lte' && cartTotal <= amount) currentRuleMatch = true;
                if (type === 'gt' && cartTotal > amount) currentRuleMatch = true;
                if (type === 'gte' && cartTotal >= amount) currentRuleMatch = true;
              } else if (rule.filter_type === 'cart_item_eq') {
                const property = rule.filter_value;
                const value = rule.param_value;
                (cartItems || []).forEach((item) => {
                  if (item && property in item && item[property] == value) {
                    currentRuleMatch = true;
                  }
                });
              } else if (rule.filter_type === 'sumo_popup') {
                if (rule.filter_value === 'exists' && popupForm) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'article') {
                if (articlesExist) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'smaller') {
                if (isNaN(parseFloat(rule.filter_value)) || width < parseFloat(rule.filter_value)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'bigger') {
                if (isNaN(parseFloat(rule.filter_value)) || width >= parseFloat(rule.filter_value)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'views_lt') {
                if (isNaN(rule.pageviews) || (pageviews < rule.pageviews)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'views_eq') {
                if (isNaN(rule.pageviews) || (pageviews === rule.pageviews)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'views_gt') {
                if (isNaN(rule.pageviews) || (pageviews > rule.pageviews)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'countryis' || (rule.filter_type === 'geolocationis' && rule.param_value === 'country')) {
                if (geoLocation.ctry && geoLocation.ctry.toLowerCase() === rule.filter_value.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'countryisnot') {
                if (!geoLocation || geoLocation.ctry.toLowerCase() !== rule.filter_value.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'geolocationis' && rule.param_value === 'state') {
                if (geoLocation.subdivision_iso && geoLocation.subdivision_name.toLowerCase() === rule.filter_value.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'geolocationis' && rule.param_value === 'city') {
                if (geoLocation.city && geoLocation.city.toLowerCase() === rule.filter_value.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'date') {
                let closestDateBefore = {};
                let closestDateAfter = {};
                const currentMoment = moment().utc();
                const currentTimezoneMoment = parseDateTimezone(rule);
                const choosenMoment = (rule.param_value === 'is_before') ?
                  currentMoment.isBefore(currentTimezoneMoment) : currentMoment.isAfter(currentTimezoneMoment);

                /**
                 * Check if rule matches for "include" rules
                 * Otherwise check to see if "exclude" rule is closest to current date time
                 * There can be over lapping rules so verify that valid closest rules to current time are applied
                 */
                if (!exclude && choosenMoment) { // Check if "include" rule is valid
                  currentRuleMatch = true;
                } else if (exclude && allRules && allRules.length) {
                  _.where(allRules, { filter_type: 'date' }).forEach((val) => {
                    const currentValueMoment = parseDateTimezone(val);
                    // Check to see if "exclude" rule starts before "now" or ends after "now"
                    if (
                      (currentMoment.isAfter(currentValueMoment) && val.param_value === 'is_after') ||
                      (currentMoment.isBefore(currentValueMoment) && val.param_value === 'is_before')
                    ) {
                      // Set 1st closestDateBefore
                      if (!closestDateBefore.rule_type && currentValueMoment.isBefore(currentMoment)) {
                        closestDateBefore = val;
                      // Set 1st closestDateAfter
                      } else if (!closestDateAfter.rule_type && currentValueMoment.isAfter(currentMoment)) {
                        closestDateAfter = val;
                      } else if (currentValueMoment.isBefore(currentMoment)) {
                        // Verify closest moment before now
                        const closestDateBeforeMoment = parseDateTimezone(closestDateBefore);
                        if (closestDateBeforeMoment.isBefore(currentValueMoment)) {
                          closestDateBefore = val;
                        }
                      } else if (currentValueMoment.isAfter(currentMoment)) {
                        // Verify closest moment after now
                        const closestDateAfterMoment = parseDateTimezone(closestDateAfter);
                        if (closestDateAfterMoment.isAfter(currentValueMoment)) {
                          closestDateAfter = val;
                        }
                      }
                    }
                  });
                  // Verify closest rules are "exclude"
                  if (closestDateBefore.rule_type === 'exclude' || closestDateAfter.rule_type === 'exclude') {
                    currentRuleMatch = true;
                  }
                }
              } else if (rule.filter_type === 'c_exists') {
                if (cookieKeysOnly.indexOf(rule.filter_value) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'c_dne') {
                if (cookieKeysOnly.indexOf(rule.filter_value) < 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'c_equals') {
                if (cookieKeysOnly.indexOf(rule.filter_value) >= 0 && cookieDict[rule.filter_value] === rule.param_value) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'c_notequals') {
                if (cookieKeysOnly.indexOf(rule.filter_value) < 0 || cookieDict[rule.filter_value] != rule.param_value) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'c_contains') {
                if (cookieKeysOnly.indexOf(rule.filter_value) >= 0 && cookieDict[rule.filter_value].indexOf(rule.param_value) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'c_notcontains') {
                if (cookieKeysOnly.indexOf(rule.filter_value) < 0 || cookieDict[rule.filter_value].indexOf(rule.param_value) < 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_exists') {
                if (currentQuery && currentQuery.hasOwnProperty(rule.filter_value.toLowerCase())) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_dne') {
                if (!currentQuery || !currentQuery.hasOwnProperty(rule.filter_value)) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_equals') {
                if (currentQuery && currentQuery[rule.filter_value] && currentQuery[rule.filter_value] === rule.param_value) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_notequals') {
                if (!currentQuery || !currentQuery[rule.filter_value] || currentQuery[rule.filter_value] != rule.param_value) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_contains') {
                if (currentQuery && currentQuery[rule.filter_value] && currentQuery[rule.filter_value].indexOf(rule.param_value) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'q_notcontains') {
                if (!currentQuery || !currentQuery[rule.filter_value] || currentQuery[rule.filter_value].indexOf(rule.param_value) < 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'exact') {
                const specialMatch = specialMatching(rule, hash, search);
                if (specialMatch) {
                  if (rule.full_url) {
                    if (path === rule.match_url) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    } else if (path === rule.match_url || pathname === rule.match_url) {
                      currentRuleMatch = true;
                    }
                  }
                }
              } else if (rule.filter_type === 'beginning') {
                const specialMatch = specialMatching(rule, hash, search);
                if (specialMatch) {
                  if (rule.full_url) {
                    if (path.indexOf(rule.match_url.toLowerCase()) === 0) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    } else if (path.indexOf(rule.match_url.toLowerCase()) === 0 || pathname.indexOf(rule.match_url.toLowerCase()) === 0) {
                      currentRuleMatch = true;
                    }
                  }
                }
              } else if (rule.filter_type === 'containing') {
                const specialMatch = specialMatching(rule, hash, search);
                if (specialMatch) {
                  if (rule.full_url) {
                    if (path.indexOf(rule.match_url.toLowerCase()) >= 0) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    } else if (path.indexOf(rule.match_url.toLowerCase()) >= 0) {
                      currentRuleMatch = true;
                    }
                  }
                }
              } else if (referrer && (['r_url_exact', 'r_domain_exact', 'r_url_beginning', 'r_domain_beginning', 'r_url_containing', 'r_domain_containing'].indexOf(rule.filter_type) >= 0)) {
                if (rule.filter_type === 'r_url_exact') {
                  const specialMatch = specialMatching(rule, referrerHash, referrerSearch);
                  if (rule.full_url) {
                    if (specialMatch && referrerMatchUrl === rule.match_url.toLowerCase()) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (specialMatch && referrerMatchUrl === rule.match_url.toLowerCase()) {
                      currentRuleMatch = true;
                    } else if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    }
                  }
                } else if (rule.filter_type === 'r_domain_exact') {
                  if (rule.host) {
                    if (rule.host === referrerHostname) {
                      currentRuleMatch = true;
                    }
                  } else if (rule.filter_value === referrerHostname) {
                    currentRuleMatch = true;
                  }
                } else if (rule.filter_type === 'r_url_beginning') {
                  const specialMatch = specialMatching(rule, referrerHash, referrerSearch);

                  if (rule.full_url) {
                    if (specialMatch && referrerMatchUrl.indexOf(rule.match_url) === 0) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (specialMatch && referrerMatchUrl.indexOf(rule.match_url) === 0) {
                      currentRuleMatch = true;
                    } else if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    }
                  }
                } else if (rule.filter_type === 'r_domain_beginning') {
                  if (rule.host && referrerHostname.indexOf(rule.host) === 0) {
                    currentRuleMatch = true;
                  } else if (referrerHostname.indexOf(rule.filter_value) === 0) {
                    currentRuleMatch = true;
                  }
                } else if (rule.filter_type === 'r_url_containing') {
                  const specialMatch = specialMatching(rule, referrerHash, referrerSearch);

                  if (rule.full_url) {
                    if (specialMatch && referrerMatchUrl.indexOf(rule.match_url) >= 0) {
                      currentRuleMatch = true;
                    }
                  } else {
                    if (specialMatch && referrerMatchUrl.indexOf(rule.match_url) >= 0) {
                      currentRuleMatch = true;
                    } else if (rule.match_url === null && specialMatch) {
                      currentRuleMatch = true;
                    }
                  }
                } else if (rule.filter_type === 'r_domain_containing') {
                  if (rule.host && referrerHostname.indexOf(rule.host) >= 0) {
                    currentRuleMatch = true;
                  } else if (referrerHostname.indexOf(rule.filter_value) >= 0) {
                    currentRuleMatch = true;
                  }
                }
              } else if (rule.filter_type === 'ua_os' && parsedUserAgent && parsedUserAgent.compareOS) {
                let valuesToCheck = [rule.filter_value];
                if (rule.filter_value === 'winmobile') {
                  valuesToCheck = ['windows mobile', 'windows phone'];
                } else if (rule.filter_value === 'macos') {
                  valuesToCheck = ['mac os'];
                } else if (rule.filter_value === 'windesktop') {
                  valuesToCheck = ['windows'];
                }

                if (valuesToCheck.indexOf(parsedUserAgent.compareOS) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'ua_browser' && parsedUserAgent && parsedUserAgent.compareBrowser) {
                let valuesToCheck = [rule.filter_value];
                if (rule.filter_value === 'chrome') {
                  valuesToCheck = ['chrome', 'chromium'];
                } else if (rule.filter_value === 'ie') {
                  valuesToCheck = ['ie', 'ie mobile'];
                } else if (rule.filter_value === 'opera') {
                  valuesToCheck = ['opera', 'opera mini', 'opera mobile', 'opera tablet'];
                } else if (rule.filter_value === 'safari') {
                  valuesToCheck = ['safari', 'mobile safari'];
                }
                if (valuesToCheck.indexOf(parsedUserAgent.compareBrowser) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'mt_equals') {
                const filterValue = rule.filter_value.replace(/:/g, '\\:');
                const metaExists = !!document.querySelector(`meta[${filterValue}="${rule.param_value}"]`);
                if (metaExists) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'mt_notequals') {
                const filterValue = rule.filter_value.replace(/:/g, '\\:');
                const metaExists = !!document.querySelector(`meta[${filterValue}="${rule.param_value}"]`);
                if (!metaExists) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'mt_contains') {
                const filterValue = rule.filter_value.replace(/:/g, '\\:');
                const metaExists = !!document.querySelector(`meta[${filterValue}*="${rule.param_value}"]`);
                if (metaExists) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'mt_notcontains') {
                const filterValue = rule.filter_value.replace(/:/g, '\\:');
                const metaExists = !!document.querySelector(`meta[${filterValue}*="${rule.param_value}"]`);
                if (!metaExists) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'mt_beginning') {
                const filterValue = rule.filter_value.replace(/:/g, '\\:');
                const metaExists = !!document.querySelector(`meta[${filterValue}^="${rule.param_value}"]`);
                if (metaExists) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'ua_exact') {
                if (rule.filter_value.toLowerCase() === navigator.userAgent.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'ua_beginning') {
                if (navigator.userAgent.toLowerCase().indexOf(rule.filter_value.toLowerCase()) === 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'ua_containing') {
                if (navigator.userAgent.toLowerCase().indexOf(rule.filter_value.toLowerCase()) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'hash_exact') {
                const filterValue = (rule.filter_value[0] === '#') ? rule.filter_value : '#' + rule.filter_value;
                if (filterValue.toLowerCase() === hash.toLowerCase()) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'hash_beginning') {
                const filterValue = (rule.filter_value[0] === '#') ? rule.filter_value : '#' + rule.filter_value;
                if (hash.toLowerCase().indexOf(filterValue.toLowerCase()) === 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'hash_containing') {
                const filterValue = (rule.filter_value[0] === '#') ? rule.filter_value : '#' + rule.filter_value;
                if (hash.toLowerCase().indexOf(filterValue.toLowerCase()) >= 0) {
                  currentRuleMatch = true;
                }
              } else if (rule.filter_type === 'js_var_exists') {
                try {
                  currentRuleMatch = eval(`typeof window.${rule.filter_value} !== 'undefined'`);
                } catch (aCold) {}
              } else if (rule.filter_type === 'js_var_dne') {
                try {
                  currentRuleMatch = eval(`typeof window.${rule.filter_value} === 'undefined'`);
                } catch (aBaseball) {}
              } else if (rule.filter_type === 'js_var_eq') {
                try {
                  currentRuleMatch = eval(safeTicks`String(window.${rule.filter_value}) === '${rule.param_value}'`);
                } catch (anException) {}
              } else if (rule.filter_type === 'js_var_notequals') {
                try {
                  currentRuleMatch = eval(safeTicks`String(window.${rule.filter_value}) != '${rule.param_value}'`);
                } catch (yaLater) {}
              } else if (rule.filter_type === 'js_var_lt') {
                try {
                  currentRuleMatch = eval(safeTicks`String(window.${rule.filter_value}) < '${rule.param_value}'`);
                } catch (aBaseball) {}
              } else if (rule.filter_type === 'js_var_gt') {
                try {
                  currentRuleMatch = eval(safeTicks`String(window.${rule.filter_value}) > '${rule.param_value}'`);
                } catch (er) {}
              } else if (rule.filter_type === 'js_eval') {
                try {
                    currentRuleMatch = eval(rule.filter_value);
                } catch (ex) {}
              } else if (rule.filter_type === 'all') {
                currentRuleMatch = true;
              }
            }
            if (!currentRuleMatch) {
              anyFail = true;
            }
          });

          if (!anyFail) {
            returnValue = true;
            break;
          }
        }

        return returnValue;
      }

      const includeRules = where(rules, 'rule_type', 'include', false);

      if (includeRules && includeRules.length) {
        const includeRuleGroups = {};
        let noGroupIdCounter = 0;
        includeRules.forEach((includeRule) => {
          if (includeRule.group_id && includeRule.group_id.length) {
            if (includeRuleGroups.hasOwnProperty(includeRule.group_id)) {
              includeRuleGroups[includeRule.group_id].push(includeRule);
            } else {
              includeRuleGroups[includeRule.group_id] = [includeRule];
            }
          } else {
            includeRuleGroups[noGroupIdCounter] = [includeRule];
            noGroupIdCounter += 1;
          }
        });

        currentUrlIncluded = checkGroups(includeRuleGroups);
      } else {
        currentUrlIncluded = true;
      }
      if (!currentUrlIncluded)
        return false;

      const excludeRules = where(rules, 'rule_type', 'exclude', false);
      let currentUrlExcluded = false;
      if (excludeRules && excludeRules.length) {
        const excludeRuleGroups = {};
        let noGroupIdCounter = 0;
        excludeRules.forEach((excludeRule) => {
          if (excludeRule.group_id && excludeRule.group_id.length) {
            if (excludeRuleGroups.hasOwnProperty(excludeRule.group_id)) {
              excludeRuleGroups[excludeRule.group_id].push(excludeRule);
            } else {
              excludeRuleGroups[excludeRule.group_id] = [excludeRule];
            }
          } else {
            excludeRuleGroups[noGroupIdCounter] = [excludeRule];
            noGroupIdCounter += 1;
          }
        });

        currentUrlExcluded = checkGroups(excludeRuleGroups, true, includeRules.concat(excludeRules));
      } else {
        return true;
      }
      if (currentUrlExcluded)
        return false;
      return true;
    } else {
      return true;
    }
  };

  return {
    isCurrentUrlAllowed,
  };

});
